import { api } from "@/shared/api/base";
import { queryClient } from "@/shared/config/queryClient";

interface PrefetchOptions {
  /** Execute requests sequentially instead of in parallel */
  sequential?: boolean;
  /** Delay between requests when using sequential mode (in ms) */
  delay?: number;
  /** Custom stale time for the query cache */
  staleTime?: number;
  /** Custom garbage collection time for the query cache */
  gcTime?: number;
}

export interface IPrefetchLink {
  url: string;
  params?: () => Record<string, any>;
}

export const usePrefetch = () => {
  const prefetchLinks = async (
    prefetchLinks: IPrefetchLink[],
    options: PrefetchOptions = {},
  ) => {
    if (!prefetchLinks || prefetchLinks?.length === 0) return;

    const {
      sequential = false,
      delay = 100,
      staleTime = 0, // 0 minutes
      gcTime = 0, // 10 minutes
    } = options;

    const prefetchSingleLink = async (link: IPrefetchLink) => {
      try {
        const params = link.params ? link.params() : {};

        await queryClient.prefetchQuery({
          queryKey: [link.url, params],
          queryFn: async () => {
            const response = await api.get(link.url, { params });
            return response;
          },
          staleTime,
          gcTime,
        });
      } catch (error) {
        // Silently handle prefetch errors to avoid breaking the UI
        console.warn(`Failed to prefetch ${link.url}:`, error);
      }
    };

    if (sequential) {
      // Execute requests one by one in order
      for (const link of prefetchLinks) {
        await prefetchSingleLink(link);
        if (delay > 0 && link !== prefetchLinks[prefetchLinks?.length - 1]) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    } else {
      // Execute all requests in parallel (original behavior)
      const prefetchPromises = prefetchLinks.map(prefetchSingleLink);
      await Promise.allSettled(prefetchPromises);
    }
  };

  return { prefetchLinks };
};
